import { Button, ConfigProvider, Drawer, Grid, Menu, Tabs, theme } from "antd";
import type { MenuProps, TabsProps } from "antd";
import { observer } from "mobx-react-lite";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { appRoutes, useAppRoutes } from "../routes/appRoutes";

import { Logo } from "./Logo";
import { LogoSmall } from "./LogoSmall";
import { UserBlock } from "./UserBlock";

import { MenuOutlined } from "@ant-design/icons";

const { useBreakpoint } = Grid;

export const HeaderContent = observer(() => {
  const { t } = useTranslation();

  const routesConfig = useAppRoutes();

  const items = useMemo(() => {
    const menuItems: NonNullable<TabsProps["items"]> = [];
    for (const c of routesConfig) {
      if (c.id && c.path && c.handle?.inMenu) {
        menuItems.push({
          key: c.path,
          label: <Link to={c.path}>{c.handle?.title}</Link>,
          id: c.path,
        });
      }
    }
    return menuItems;
  }, [routesConfig]);

  const drawerItems = useMemo<NonNullable<MenuProps["items"]>>(
    () =>
      items.map((item) => ({
        key: item.key,
        label: item.label,
      })),
    [items],
  );

  const screens = useBreakpoint();
  const location = useLocation();
  const { token } = theme.useToken();

  const activeTab = items.find((item) => item.id === location.pathname);
  const activeKey = activeTab?.key;

  const navigate = useNavigate();

  const onTabClick = (key: string) => {
    navigate(key);
    setDrawerOpened(false);
  };

  const [drawerOpened, setDrawerOpened] = useState(false);

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: token.padding,
        }}
      >
        {!screens.lg ? (
          <>
            <Button
              type="text"
              icon={<MenuOutlined style={{ color: token.colorIcon }} />}
              onClick={() => setDrawerOpened(true)}
            />
            <LogoSmall />
          </>
        ) : (
          <Logo />
        )}
        {!screens.xs && (
          <div
            style={{
              width: "2px",
              height: "36px",
              backgroundColor: token.colorSplit,
            }}
          />
        )}
        <Link
          style={{
            color: token.colorPrimary,
            fontSize: token.fontSizeLG,
            fontWeight: token.fontWeightStrong,
          }}
          to={appRoutes.mainPage}
        >
          {t("navigation.title")}
        </Link>
      </div>

      <Drawer
        size={240}
        placement="left"
        closable={false}
        open={drawerOpened}
        onClose={() => setDrawerOpened(false)}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            padding: token.paddingSM,
          },
        }}
      >
        <Menu
          mode="inline"
          items={drawerItems}
          selectedKeys={activeKey ? [activeKey] : []}
          onClick={() => setDrawerOpened(false)}
          style={{
            borderInlineEnd: 0,
          }}
        />

        <div
          style={{
            marginTop: "auto",
            padding: token.paddingSM,
          }}
        >
          <Link to={appRoutes.create} onClick={() => setDrawerOpened(false)}>
            <Button type="primary" block>
              {t("navigation.create")}
            </Button>
          </Link>
        </div>
      </Drawer>

      {screens.lg && (
        <ConfigProvider
          theme={{
            token: {
              colorLink: token.colorText,
              colorLinkHover: token.colorLinkHover,
            },
          }}
        >
          <Tabs
            activeKey={activeKey}
            items={items}
            styles={{
              root: { height: "50px" },
              item: { height: "50px" },
              indicator: { display: activeKey ? "block" : "none" },
            }}
            moreIcon={null}
            onTabClick={onTabClick}
          />
        </ConfigProvider>
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: token.padding,
          marginInlineStart: "auto",
        }}
      >
        {screens.lg && (
          <Link to={appRoutes.create}>
            <Button type="primary">{t("navigation.create")}</Button>
          </Link>
        )}

        <UserBlock />
      </div>
    </>
  );
});

HeaderContent.displayName = "HeaderContent";
